// Read-only, selected-week evidence. Never supplements archives with live data.
import { PROJECT_TRACKS, canonicalTrack } from "./projectTracks.js";

export const TEAM_ROLES = [
  { key: "delivery_owner", label: "Delivery Owner (Ripple)", sections: "Overall status" },
  { key: "pmo_pm", label: "PMO PM (JLL)", sections: "Schedule, PMO note" },
  { key: "market_pm", label: "Market PM (JLL)", sections: "Financials, Decisions, Risks, Achievements & upcoming" },
  ...PROJECT_TRACKS.map(t => ({ ...t, sections: `${t.label} track` })),
];
const date = value => typeof value === "string" ? value.slice(0, 10) : "";
const stamp = value => Number.isFinite(Date.parse(value)) ? Date.parse(value) : null;
export function weeklyTeam(project, week) {
  const evidence = project?.teamEvidence;
  const assignments = evidence?.version === 1 && evidence.weekDate === week && Array.isArray(evidence.assignments)
    ? evidence.assignments : project?.participants;
  const assignmentsAvailable = Array.isArray(assignments);
  const identitiesAvailable = assignmentsAvailable && assignments.every(p =>
    p.active === false || p.postEligible === false || Boolean(p.personId));
  const historyAvailable = evidence?.version === 1 && Array.isArray(evidence.posts) && evidence.weekDate === week;
  const end = stamp(`${week}T00:00:00Z`) + 7 * 86400000;
  const posts = historyAvailable ? evidence.posts.filter(p => p.projectId === project.id && p.personId
    && date(p.weekDate) <= week && stamp(p.createdAt) !== null && stamp(p.createdAt) < end) : [];
  const people = new Map();
  for (const p of assignmentsAvailable ? assignments : []) {
    if (p.active === false || p.postEligible === false || !p.personId) continue;
    if (!people.has(p.personId)) people.set(p.personId, { ...p });
  }
  const describe = p => {
    const mine = posts.filter(post => post.personId === p.personId).sort((a, b) => stamp(b.createdAt) - stamp(a.createdAt));
    const current = mine.filter(post => date(post.weekDate) === week);
    return { ...p, posted: historyAvailable && p.personId && p.active !== false && p.postEligible !== false ? current.length > 0 : null,
      postedAt: current[0]?.createdAt || null, lastPostedAt: mine[0]?.createdAt || null,
      historyAvailable };
  };
  const rows = assignmentsAvailable ? TEAM_ROLES.flatMap(role => {
    const assigned = assignments.filter(p => (p.trackKey ? canonicalTrack(p.trackKey) : p.ownershipSlot) === role.key)
      .sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)));
    return assigned.length ? assigned.map((p, i) => ({ ...role, ...describe(p), rowKey: `${role.key}-${p.personId || i}` }))
      : [{ ...role, rowKey: role.key, unassigned: true }];
  }).sort((a, b) => Number(Boolean(a.unassigned)) - Number(Boolean(b.unassigned))) : [];
  const owners = Object.fromEntries(TEAM_ROLES.map(role => {
    const all = rows.filter(r => r.key === role.key && !r.unassigned);
    const primary = all.filter(r => r.isPrimary);
    return [role.key, primary.length === 1 ? primary : all];
  }));
  const distinct = [...people.values()].map(describe);
  return { assignmentsAvailable, historyAvailable, rows, owners,
    people: distinct, posted: identitiesAvailable && historyAvailable ? distinct.filter(p => p.posted).length : null,
    total: identitiesAvailable ? distinct.length : null,
    // Resolve original author identity only, never a corrector/updater or owner label.
    contributor(item, roleKey) {
      if (!assignmentsAvailable || !Object.hasOwn(owners, roleKey) || !item?.authorPersonId
        || owners[roleKey].some(p => !p.personId || p.personId === item.authorPersonId)) return null;
      const person = people.get(item.authorPersonId) || (item.authorName
        ? { personId: item.authorPersonId, displayName: item.authorName, org: item.authorOrg } : null);
      return person ? { ...describe(person), label: "Contributor", sections: "Authored update" } : null;
    } };
}
